import type { FieldWithCropzone } from '@protector/entities/region/field-cropzone.models';
import type { UUID } from '@protector/utils/types';
import dayjs from 'dayjs';
import { captureError } from 'infra/monitoring/monitoring.utils';
import {
  FIELDS_BY_SEASONS_STORE_NAME,
  FIELDS_BY_SEASONS_SYNC_META_STORE_NAME,
  IndexedDBPromiseAPI,
  PROPERTY_ID_INDEX,
  SYNC_META_PROPERTY_ID_INDEX
} from '../../core/indexeddb.utils';
import { getSeasonFieldReferenceDate } from './region.utils';

export const NO_CROPZONE_SEASON_ID = '__no_cropzone__';

export interface StoredFieldWithCropzone extends FieldWithCropzone {
  seasonId: string;
}

export interface StoredSyncWatermark {
  propertyId: UUID;
  seasonId: string;
  lastCalledAt: string;
}

export interface FieldsCropzoneCoverage {
  lastSyncedAt: string;
}

export interface FieldsCropzoneCacheResult {
  fields: FieldWithCropzone[];
  /**
   * Resolves once the field rows for this call have been persisted (or immediately, if there was
   * nothing to persist); rejects if that write failed. Callers must not record a sync watermark
   * until this resolves — otherwise a future request could see a watermark for a season whose
   * field data never actually made it into the cache, and wrongly pick `/sync` over a full fetch.
   */
  persisted: Promise<void>;
}

const resolveFieldSeasonId = (field: FieldWithCropzone): string => field.cropzone?.season_id ?? NO_CROPZONE_SEASON_ID;

/** Joined, sorted season-id key — see `computeFieldsCropzoneCoverage` for why the watermark is keyed this way. */
const compareSeasonIds = (a: UUID, b: UUID): number => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const seasonSetKey = (seasonIds: UUID[]): string => [...new Set(seasonIds)].sort(compareSeasonIds).join(',');

export const getCachedFieldsForProperty = async (propertyId: UUID): Promise<StoredFieldWithCropzone[]> => {
  const stored = (await IndexedDBPromiseAPI.queryIndex(FIELDS_BY_SEASONS_STORE_NAME, PROPERTY_ID_INDEX, propertyId)) as
    | StoredFieldWithCropzone[]
    | undefined;
  return stored ?? [];
};

export const getSyncWatermarksForProperty = async (propertyId: UUID): Promise<StoredSyncWatermark[]> => {
  const stored = (await IndexedDBPromiseAPI.queryIndex(FIELDS_BY_SEASONS_SYNC_META_STORE_NAME, SYNC_META_PROPERTY_ID_INDEX, propertyId)) as
    | StoredSyncWatermark[]
    | undefined;
  return stored ?? [];
};

/**
 * Wipes this property's cached field rows and sync watermarks. Called when the
 * `P40_62642_use_fields_by_seasons_cache` flag is off, so a property that had the cache populated
 * while the flag was on doesn't keep serving `/sync` off stale local data once it's back on, and so
 * disabling the flag actually stops the local store from growing instead of just no longer reading it.
 */
export const clearFieldsCropzoneCacheForProperty = async (propertyId: UUID): Promise<void> => {
  const [cachedFields, watermarks] = await Promise.all([getCachedFieldsForProperty(propertyId), getSyncWatermarksForProperty(propertyId)]);
  await Promise.all([
    cachedFields.length
      ? IndexedDBPromiseAPI.deleteValues(
          FIELDS_BY_SEASONS_STORE_NAME,
          cachedFields.map(field => [field.seasonId, field.id])
        )
      : Promise.resolve(),
    watermarks.length
      ? IndexedDBPromiseAPI.deleteValues(
          FIELDS_BY_SEASONS_SYNC_META_STORE_NAME,
          watermarks.map(watermark => [watermark.propertyId, watermark.seasonId])
        )
      : Promise.resolve()
  ]);
};

/**
 * Called once a `/by-seasons` or `/by-seasons/sync` request for these seasons has succeeded.
 *
 * Doesn't invalidate other stored watermarks whose combination shares a season with this one. A
 * combination fetched *before* a wider one that collapses a shared season differently would be
 * left stale by this — see the PR discussion on this line for why that specific ordering can't
 * occur (property selection always fetches the full active-seasons combination first).
 */
export const recordSyncWatermark = (propertyId: UUID, seasonIds: UUID[], calledAt: string): void => {
  if (!seasonIds.length) return;
  const record: StoredSyncWatermark = { propertyId, seasonId: seasonSetKey(seasonIds), lastCalledAt: calledAt };
  IndexedDBPromiseAPI.insertValues(FIELDS_BY_SEASONS_SYNC_META_STORE_NAME, [record]).catch(error =>
    captureError(error, { errorType: 'IndexedDBError', domain: 'Region' })
  );
};

const persistFields = (content: FieldWithCropzone[]): Promise<void> => {
  if (!content.length) return Promise.resolve();
  const tagged: StoredFieldWithCropzone[] = content.map(field => ({ ...field, seasonId: resolveFieldSeasonId(field) }));
  return IndexedDBPromiseAPI.insertValues(FIELDS_BY_SEASONS_STORE_NAME, tagged)
    .then(() => undefined)
    .catch(error => {
      captureError(error, { errorType: 'IndexedDBError', domain: 'Region' });
      throw error;
    });
};

const seasonAndIdKey = (seasonId: string, id: UUID): string => `${seasonId}::${id}`;

/**
 * `validKeys` must be `[seasonId, id]` pairs, not bare ids: storage is keyed per season, and a
 * field can legitimately have a distinct cropzone row in more than one requested season at once.
 * Keying eviction by id alone would delete a still-valid row for one season just because the
 * same field id was pruned from another.
 */
const evictStaleFields = (relevantSeasonIds: string[], validKeys: Set<string>, stored: StoredFieldWithCropzone[]): void => {
  const relevantSet = new Set(relevantSeasonIds);
  const toEvict = stored.filter(field => relevantSet.has(field.seasonId) && !validKeys.has(seasonAndIdKey(field.seasonId, field.id)));
  if (!toEvict.length) return;
  const keys = toEvict.map(field => [field.seasonId, field.id]);
  IndexedDBPromiseAPI.deleteValues(FIELDS_BY_SEASONS_STORE_NAME, keys).catch(error =>
    captureError(error, { errorType: 'IndexedDBError', domain: 'Region' })
  );
};

const CROPZONE_PRIORITY_CURRENT = 0;
const CROPZONE_PRIORITY_ENDED = 1;
const CROPZONE_PRIORITY_FUTURE = 2;
const CROPZONE_PRIORITY_NONE = 3;

const cropzonePriority = (cropzone: FieldWithCropzone['cropzone'], today: string): number => {
  if (!cropzone) return CROPZONE_PRIORITY_NONE;
  const endsAt = dayjs.utc(cropzone.ends_at);
  if (!endsAt.isValid()) return CROPZONE_PRIORITY_NONE;
  if (endsAt.format('YYYY-MM-DD') < today) return CROPZONE_PRIORITY_ENDED;
  const startsAt = dayjs.utc(cropzone.starts_at);
  if (startsAt.isValid() && startsAt.format('YYYY-MM-DD') > today) return CROPZONE_PRIORITY_FUTURE;
  return CROPZONE_PRIORITY_CURRENT;
};

/**
 * A field can show up more than once across the requested seasons — e.g. one season's cropzone
 * running today, another already ended and a third only starting next month. Per id, the
 * highest-priority row wins: current cropzone > ended cropzone > cropzone that has not started >
 * no cropzone (including an unparseable end date). That order is what decides which single state
 * the field is shown in, so it is declared here rather than emerging from which rows the API
 * happens to return. Ties (e.g. two current rows) fall back to whichever row was most recently
 * updated.
 */
export const selectActiveFieldPerId = (fields: FieldWithCropzone[], propertyTimeZone?: string): FieldWithCropzone[] => {
  const today = getSeasonFieldReferenceDate(undefined, propertyTimeZone);
  const byId = new Map<UUID, FieldWithCropzone>();
  fields.forEach(field => {
    const current = byId.get(field.id);
    if (!current) {
      byId.set(field.id, field);
      return;
    }
    const currentPriority = cropzonePriority(current.cropzone, today);
    const fieldPriority = cropzonePriority(field.cropzone, today);
    const isMorePriority = fieldPriority < currentPriority;
    const isMoreRecentTie = fieldPriority === currentPriority && Date.parse(field.updated_at) > Date.parse(current.updated_at);
    if (isMorePriority || isMoreRecentTie) byId.set(field.id, field);
  });
  return [...byId.values()];
};

/**
 * Pure: takes the property's already-read watermark rows so callers that also need those rows for
 * priming/syncing (see below) don't have to read IndexedDB a second time for the same request.
 *
 * `lastSyncedAt` is the client's own record of when it last called `/by-seasons` or
 * `/by-seasons/sync` for the exact combination of seasons being requested now — not derived from
 * the fields' `updated_at`. A combination with no matching watermark row (never fetched with this
 * exact season set, or evicted alongside the field cache) forces a full re-fetch instead of
 * guessing a watermark.
 *
 * The watermark is keyed by the *exact* set of requested seasons on purpose: the backend resolves
 * a field to a single cropzone across a whole request when several seasons are combined, so a
 * field can come back tagged to season A and be entirely absent from the response for season B —
 * even though it legitimately belongs to both. If coverage for B were inferred from a watermark
 * recorded while B was requested alongside A, `/sync` would trust that B is fully covered locally
 * and never backfill that field, since nothing about it ever "changes" in a later delta. Requiring
 * an exact match means narrowing (or widening) the season selection always forces a full fetch
 * the first time that specific combination is requested, and only reuses `/sync` for revisiting a
 * combination already fetched in full before.
 *
 * The cached-rows check on top of that is a cheap integrity guard against a watermark surviving
 * while the field cache it refers to was wiped or never persisted — it only requires *some* row
 * tagged under one of the requested seasons, not one under every single season in the combination.
 * The backend legitimately resolving every field under season A within an [A, B] request (nothing
 * under B) is the correct, complete result for that exact combination, not a coverage gap — an
 * `every` check here would keep forcing a full fetch for that combination forever, even though the
 * matching watermark already proves it was fetched to completion before.
 */
export const computeFieldsCropzoneCoverage = (
  watermarks: StoredSyncWatermark[],
  cachedFields: StoredFieldWithCropzone[],
  seasonIds: UUID[]
): FieldsCropzoneCoverage | undefined => {
  if (!seasonIds.length || !watermarks.length || !cachedFields.length) return undefined;

  const cachedSeasonIds = new Set(cachedFields.map(field => field.seasonId));
  if (!seasonIds.some(seasonId => cachedSeasonIds.has(seasonId))) return undefined;

  const watermark = watermarks.find(candidate => candidate.seasonId === seasonSetKey(seasonIds));
  if (!watermark) return undefined;

  return Number.isFinite(Date.parse(watermark.lastCalledAt)) ? { lastSyncedAt: watermark.lastCalledAt } : undefined;
};

export const primeFieldsCropzoneCache = (
  seasonIds: UUID[],
  content: FieldWithCropzone[],
  stored: StoredFieldWithCropzone[]
): FieldsCropzoneCacheResult => {
  const validKeys = new Set(content.map(field => seasonAndIdKey(resolveFieldSeasonId(field), field.id)));
  evictStaleFields([...seasonIds, NO_CROPZONE_SEASON_ID], validKeys, stored);
  return { fields: content, persisted: persistFields(content) };
};

export const applyFieldsCropzoneSync = (
  seasonIds: UUID[],
  content: FieldWithCropzone[],
  fieldIds: UUID[],
  stored: StoredFieldWithCropzone[]
): FieldsCropzoneCacheResult => {
  const relevantSeasonIds = new Set([...seasonIds, NO_CROPZONE_SEASON_ID]);

  if (!fieldIds.length && stored.some(field => relevantSeasonIds.has(field.seasonId))) {
    captureError(new Error('fields-by-seasons /sync returned an empty field_ids list while cached data exists for these seasons'), {
      errorType: 'IndexedDBError',
      domain: 'Region'
    });
  }

  const relevantStored = stored.filter(field => relevantSeasonIds.has(field.seasonId));
  const validIds = new Set(fieldIds);

  if (!content.length && relevantStored.every(field => validIds.has(field.id))) {
    const fields = relevantStored.map(field => {
      const withoutSeasonId: Partial<StoredFieldWithCropzone> = { ...field };
      delete withoutSeasonId.seasonId;
      return withoutSeasonId as FieldWithCropzone;
    });
    return { fields, persisted: Promise.resolve() };
  }

  const fieldsBySeasonAndId = new Map<string, FieldWithCropzone>(
    relevantStored.map(({ seasonId: storedSeasonId, ...field }) => [seasonAndIdKey(storedSeasonId, field.id), field as FieldWithCropzone])
  );

  content.forEach(field => {
    const newKey = seasonAndIdKey(resolveFieldSeasonId(field), field.id);
    fieldsBySeasonAndId.set(newKey, field);
  });

  [...fieldsBySeasonAndId.entries()].forEach(([key, field]) => {
    if (!validIds.has(field.id)) fieldsBySeasonAndId.delete(key);
  });

  evictStaleFields([...seasonIds, NO_CROPZONE_SEASON_ID], new Set(fieldsBySeasonAndId.keys()), stored);

  return { fields: [...fieldsBySeasonAndId.values()], persisted: persistFields(content) };
};
